/**
 * /operations/loans/settlements — Session 3a PR 12.
 *
 * Early-closure requests — employees paying off loans before scheduled
 * end. Rawatib-owned per Session 3a §4.11 (money + WPS settlement
 * agent).
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Rawatib narration)
 *   2. InlineAISuggestion cards (EOS coordination · early-payment
 *      discount eligibility)
 *   3. TraceableValue wrapping outstanding + proposed_settlement +
 *      request_date (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { ChevronRight, HandCoins, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  EntityDrawer,
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type SettlementItem,
} from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { formatSar, severityChip, showAlertTrace } from "./_session3aHelpers";

type Status = "pending_approval" | "approved_unpaid" | "paid_30d" | "denied" | "all";

function NewSettlementDrawer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    employee_id: "",
    application_id: "",
    proposed_settlement: "",
    reason: "",
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("operations.loans_settlements.cta.new", "Initiate settlement")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            disabled={!form.employee_id || !form.application_id || !form.reason}
            onClick={() => {
              showAlertTrace(
                t(
                  "operations.loans_settlements.created",
                  "Settlement will route through M15.5 chain (workflow_type='loan_settlement'); wiring lands with POST /api/loans/settlements.",
                ),
              );
              onClose();
            }}
            className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("operations.loans_settlements.initiate", "Initiate")}
          </button>
        </div>
      }
    >
      <div className="space-y-4 p-4 text-sm">
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("operations.loans_settlements.field.employee", "Employee")}
          </label>
          <input
            value={form.employee_id}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t(
              "operations.loans_settlements.field.application",
              "Active loan application",
            )}
          </label>
          <input
            value={form.application_id}
            onChange={(e) => setForm({ ...form, application_id: e.target.value })}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t(
              "operations.loans_settlements.field.proposed",
              "Proposed settlement amount (SAR)",
            )}
          </label>
          <input
            type="number"
            value={form.proposed_settlement}
            onChange={(e) =>
              setForm({ ...form, proposed_settlement: e.target.value })
            }
            placeholder="Default = outstanding"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("operations.loans_settlements.field.reason", "Reason")}
          </label>
          <textarea
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            rows={3}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          />
          <button
            type="button"
            onClick={() =>
              setForm((f) => ({
                ...f,
                reason: t(
                  "operations.loans_settlements.draft_reason",
                  "Employee requested early settlement to clear obligations before EOS / job change.",
                ),
              }))
            }
            className="mt-1 text-[11px] text-[#2260C9] hover:underline"
          >
            ✦ {t("operations.loans_settlements.draft", "Rawatib draft reason")}
          </button>
        </div>
      </div>
    </EntityDrawer>
  );
}

export function LoanSettlementsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [status, setStatus] = useState<Status>("pending_approval");
  const [showDrawer, setShowDrawer] = useState(false);

  const list = useQuery({
    queryKey: ["loan-settlements", legalEntityId, status],
    queryFn: () =>
      session3aApi.listSettlements(
        legalEntityId ?? "",
        status === "all" ? undefined : status,
      ),
    enabled: !!legalEntityId,
  });

  const items: SettlementItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const pending = items.filter((r) => r.status === "pending_approval");
    const sumPending = pending.reduce((acc, r) => acc + r.proposed_settlement_sar, 0);
    const withDiscount = items.filter(
      (r) => r.proposed_settlement_sar < r.outstanding_at_request_sar,
    );
    return {
      total: items.length,
      pending: pending.length,
      sumPending,
      withDiscount: withDiscount.length,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.loans_settlements.title", "Early Settlements")}
          subtitle={t(
            "operations.loans_settlements.subtitle",
            "Early-closure loan requests with early-payment discount support. Rawatib computes the money math; M15.5 chain routes the approval.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["pending_approval", "Pending approval"],
                ["approved_unpaid", "Approved unpaid"],
                ["paid_30d", "Paid (30d)"],
                ["denied", "Denied"],
                ["all", "All"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatus(key as Status)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  status === key
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`operations.loans_settlements.filter.${key}`, label)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowDrawer(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <HandCoins className="h-4 w-4" />
            {t("operations.loans_settlements.cta.new", "Initiate settlement")}
          </button>
        </div>

        <InlineAISuggestion
          agent="rawatib"
          icon={<Sparkles className="h-4 w-4" />}
          severity="info"
          headline={t(
            "operations.loans_settlements.suggestion.eos_headline",
            "Rawatib note: EOS coordination check.",
          )}
          body={t(
            "operations.loans_settlements.suggestion.eos_body",
            "If the settlement falls within an employee's final-payslip month, route it through the EOS workflow so the deduction lands cleanly.",
          )}
          primaryAction={{
            label: t(
              "operations.loans_settlements.suggestion.open_eos",
              "Open EOS queue",
            ),
            onClick: () => window.location.assign("/operations/eos"),
          }}
          suggestionKey="loan-settlements-eos"
        />
        {stats.withDiscount > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<HandCoins className="h-4 w-4" />}
            severity="info"
            headline={t(
              "operations.loans_settlements.suggestion.discount_headline",
              `Discount policy: ${stats.withDiscount} settlements qualify for 5% early-payment incentive.`,
            )}
            body={t(
              "operations.loans_settlements.suggestion.discount_body",
              "Auto-apply or send a tailored offer via Khidmat — Rawatib walks the math.",
            )}
            primaryAction={{
              label: t(
                "operations.loans_settlements.suggestion.apply_discount",
                "Apply discount",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "operations.loans_settlements.suggestion.discount_note",
                    "Discount rule lives in product policy; toggle wires to /api/loans/settlements/discount-apply.",
                  ),
                ),
            }}
            suggestionKey={`loan-settlements-discount-${stats.withDiscount}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("operations.loans_settlements.col.settlement", "Settlement")}</th>
                  <th className="px-3 py-2">{t("operations.loans_settlements.col.application", "Application")}</th>
                  <th className="px-3 py-2">{t("operations.loans_settlements.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("operations.loans_settlements.col.outstanding", "Outstanding")}</th>
                  <th className="px-3 py-2">{t("operations.loans_settlements.col.proposed", "Proposed")}</th>
                  <th className="px-3 py-2">{t("operations.loans_settlements.col.request_date", "Request date")}</th>
                  <th className="px-3 py-2">{t("operations.loans_settlements.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("operations.loans_settlements.col.approver", "Approver")}</th>
                  <th className="px-3 py-2">{t("common.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.settlement_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_settlements.detail_note",
                              `Settlement ${row.settlement_id.slice(0, 8)}... opens drawer with full workflow trail.`,
                            ),
                          )
                        }
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.settlement_id.slice(0, 8)}…
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/operations/loans/applications/${row.application_id}`}
                        className="text-text-secondary hover:underline"
                      >
                        {row.application_id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="text-text-secondary hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_settlements.trace.outstanding",
                              `outstanding_at_request = principal − installments_paid + interest_accrued at the request timestamp.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">{formatSar(row.outstanding_at_request_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_settlements.trace.proposed",
                              `proposed = outstanding − early_payment_discount (if product policy enables). Rawatib computes the discount per policy.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr" className="font-medium text-text-primary">
                          {formatSar(row.proposed_settlement_sar)}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_settlements.trace.request",
                              `Requested ${row.request_date}. Workflow_events capture the full audit.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">{row.request_date.slice(0, 10)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.status === "pending_approval"
                            ? "attention"
                            : row.status === "approved"
                              ? "info"
                              : row.status === "paid"
                                ? "neutral"
                                : "critical",
                        )}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      {row.approver_name ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/operations/loans/applications/${row.application_id}`}
                        className="rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                      >
                        {t("common.open", "Open")}
                        <ChevronRight className="ms-0.5 inline h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.loans_settlements.empty",
              "No early settlements requested — loans are running their full term.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: stats.total,
            attention: stats.pending,
            critical: 0,
            top_kind: t("operations.loans_settlements.title", "Early Settlements"),
            window_label: status,
          }}
        />
      </aside>
      {showDrawer && <NewSettlementDrawer onClose={() => setShowDrawer(false)} />}
    </div>
  );
}
